import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { MoneyDisplay } from '@/components/ui/Typography';
import { paymentsApi, type CardTopupOut } from '@/api/payments';
import { cardsApi, formatCardLabel, isCardsUnavailable, type SavedCard } from '@/api/cards';
import { walletApi, type WalletOut } from '@/api/wallet';
import { encryptCard, CardValidationError } from '@/lib/pagbankSdk';
import { executeRecaptcha } from '@/lib/recaptcha';
import { friendlyMessage } from '@/lib/errors';
import { formatBRL } from '@/lib/format';
import { StepFrame, StepHeader } from '../RechargePix';

const MUTED = 'rgba(0,27,116,0.55)';
const DANGER = '#D5152B';
const POLL_MS = 3000;
const POLL_MAX_MS = 60000;
const RECAPTCHA_ACTION = 'card_topup'; // deve casar com settings.recaptcha_action

type Ui =
  | 'loading' // buscando a chave pública
  | 'sdk_error' // SDK/chave indisponível → só resta PIX
  | 'form'
  | 'processing' // encrypt + POST
  | 'polling' // in_analysis / 3DS pendente
  | 'paid_no_wallet' // creditou mas a releitura da carteira falhou
  | 'declined';

interface Props {
  amount: number;
  cashbackValue: number;
  onPaid: (wallet: WalletOut) => void;
  onBack: () => void;
  onFallbackToPix: () => void;
  fallbackWallet: WalletOut | null;
  onDone: () => void;
  showDevTools?: boolean;
  /** Veio de "Adicionar cartão": abre direto o formulário, mesmo havendo cartão salvo. */
  startWithNewCard?: boolean;
  /** Valor inicial do "Salvar este cartão pra próxima" (o cliente ainda pode desligar). */
  defaultStoreCard?: boolean;
}

/**
 * Passo 3/3 — pagamento com cartão. O PAN/CVV nunca sobem: o SDK do PagBank
 * criptografa no aparelho (`encryptCard`) e o backend recebe só o blob.
 * Falha de SDK/chave vira estado visível com saída pro PIX (§10).
 */
export default function CardStep({
  amount,
  cashbackValue,
  onPaid,
  onBack,
  onFallbackToPix,
  fallbackWallet,
  onDone,
  showDevTools,
  startWithNewCard = false,
  defaultStoreCard = false
}: Props) {
  const [ui, setUi] = useState<Ui>('loading');
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastChargeId, setLastChargeId] = useState<string | null>(null);

  const [number, setNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [holder, setHolder] = useState('');

  // Cartão salvo (atrás de card_save_enabled): a lista some com 404 quando a
  // flag está off → `saveEnabled=false` e o app não mostra nada de salvar.
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [usingNew, setUsingNew] = useState(startWithNewCard); // "usar outro cartão"
  const [storeCard, setStoreCard] = useState(defaultStoreCard); // salvar o cartão digitado

  const cancelled = useRef(false);
  // Trava de reentrância: setUi('processing') é assíncrono, então dois toques
  // rápidos no CTA passariam pelo disabled antes do re-render → duas cobranças.
  const inFlight = useRef(false);
  useEffect(() => () => { cancelled.current = true; }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const resp = await paymentsApi.getCardPublicKey();
        if (!alive) return;
        setPublicKey(resp.public_key);
        setRecaptchaSiteKey(resp.recaptcha_site_key ?? null);
        setUi('form');
      } catch {
        if (alive) setUi('sdk_error');
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Cartões salvos — best-effort, independente da chave pública. Se a flag
  // card_save_enabled estiver off, a lista responde 404 → nada de cartão salvo
  // (nem picker, nem checkbox). Falha real também degrada pra "sem salvos".
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cards = await cardsApi.list();
        if (!alive) return;
        setSavedCards(cards);
        setSaveEnabled(true);
      } catch (err) {
        if (!alive) return;
        if (!isCardsUnavailable(err)) {
          /* erro transitório: mantém saveEnabled=false, sem travar o fluxo */
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function finishPaid() {
    // Creditou de verdade — o cliente NUNCA pode ficar preso na tela. Relê a
    // carteira (com retry) pro PaidPhase; se falhar, libera com a carteira que
    // já tínhamos (o /wallet reconcilia); no pior caso, um estado de sucesso
    // explícito com saída.
    for (let i = 0; i < 3; i++) {
      const wallet = await walletApi.get().catch(() => null);
      if (cancelled.current) return;
      if (wallet) {
        onPaid(wallet);
        return;
      }
      await new Promise((r) => setTimeout(r, 800));
    }
    if (cancelled.current) return;
    if (fallbackWallet) onPaid(fallbackWallet);
    else setUi('paid_no_wallet');
  }

  async function pollUntilTerminal(chargeId: string) {
    setUi('polling');
    const started = Date.now();
    while (!cancelled.current && Date.now() - started < POLL_MAX_MS) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      if (cancelled.current) return;
      try {
        const st = await paymentsApi.getCardCharge(chargeId);
        if (st.status === 'paid') return finishPaid();
        if (st.status === 'declined' || st.status === 'failed') {
          setError(st.decline_message || 'Cartão recusado.');
          setUi('declined');
          return;
        }
      } catch {
        /* transiente — segue tentando dentro da janela */
      }
    }
    // Estourou o teto: deixa o cliente saber, sem travar.
    setError('Estamos confirmando o pagamento. Você recebe um aviso — o saldo aparece na carteira.');
    setUi('declined');
  }

  function handleResult(res: CardTopupOut) {
    setLastChargeId(res.charge_id);
    if (res.status === 'paid') return finishPaid();
    // 'failed' = pedido rejeitado na entrada (nada cobrado); 'declined' =
    // recusa da adquirente. Ambos são TERMINAIS — nunca cair no polling (o
    // 'failed' nunca vira 'paid', prenderia a tela em "Confirmando…").
    if (res.status === 'declined' || res.status === 'failed') {
      setError(res.decline_message || 'Cartão recusado. Confira os dados ou use outro cartão.');
      setUi('declined');
      return;
    }
    // in_analysis / created / authorized → polling
    return pollUntilTerminal(res.charge_id);
  }

  // Token do reCAPTCHA v3, best-effort: sem site_key (recurso off) → undefined;
  // falha de script/rede → undefined também (segue sem token; o backend julga).
  async function getRecaptchaToken(): Promise<string | undefined> {
    if (!recaptchaSiteKey) return undefined;
    try {
      return await executeRecaptcha(recaptchaSiteKey, RECAPTCHA_ACTION);
    } catch {
      return undefined;
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!publicKey || inFlight.current) return;
    inFlight.current = true;
    setError(null);
    setUi('processing');
    const [mm, yy] = expiry.split('/');
    try {
      const encrypted = await encryptCard({
        publicKey,
        holder: holder.trim(),
        number: number.replace(/\s+/g, ''),
        expMonth: (mm || '').trim(),
        expYear: yy ? `20${yy.trim()}` : '',
        securityCode: cvv.trim()
      });
      const res = await paymentsApi.createCard({
        amount_brl: amount.toFixed(2),
        encrypted_card: encrypted,
        // Salvar só quando a flag permite E o cliente pediu (tokenize-on-charge).
        store_card: saveEnabled && storeCard,
        recaptcha_token: await getRecaptchaToken()
      });
      // status paid/declined/created — o backend nunca devolve 202 (o timeout
      // vem como 'created' com charge_id, que o handleResult polla).
      await handleResult(res);
    } catch (err) {
      if (err instanceof CardValidationError) {
        setError('Confira o número, a validade e o CVV do cartão.');
        setUi('form');
        return;
      }
      // Falha de SDK/rede/infra → não trava a tela: erro visível + saída PIX.
      setError(friendlyMessage(err, 'Não foi possível concluir no cartão.'));
      setUi('form');
    } finally {
      inFlight.current = false;
    }
  }

  // Pagar de UM toque com um cartão salvo (cobrança com o token — sem redigitar
  // e sem CVV; o gateway aceita, provado em sandbox s12).
  async function onPaySaved(card: SavedCard) {
    if (inFlight.current) return;
    inFlight.current = true;
    setError(null);
    setUi('processing');
    try {
      const res = await paymentsApi.createCard({
        amount_brl: amount.toFixed(2),
        saved_card_id: card.id,
        recaptcha_token: await getRecaptchaToken()
      });
      await handleResult(res);
    } catch (err) {
      setError(friendlyMessage(err, 'Não foi possível cobrar esse cartão.'));
      setUi('form');
    } finally {
      inFlight.current = false;
    }
  }

  const canSubmit =
    number.replace(/\s+/g, '').length >= 13 &&
    /^\d{2}\/\d{2}$/.test(expiry) &&
    cvv.length >= 3 &&
    holder.trim().length >= 3;

  if (ui === 'sdk_error') {
    return (
      <StepFrame kbHeight={null}>
        <StepHeader title="Cartão" step="3/3" onBack={onBack} />
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-2 text-center">
          <div className="text-[15px] font-semibold text-bow-royal">
            Não conseguimos carregar o pagamento com cartão
          </div>
          <p className="text-[13px]" style={{ color: MUTED }}>
            Você pode adicionar saldo por PIX — cai na hora.
          </p>
        </div>
        <div className="flex-shrink-0 pb-2.5 pt-2.5">
          <Button variant="primary" onClick={onFallbackToPix}>
            Usar PIX
          </Button>
        </div>
      </StepFrame>
    );
  }

  if (ui === 'paid_no_wallet') {
    // Pagamento aprovado e creditado; só não conseguimos reler o saldo aqui.
    // O cliente é liberado (nunca preso) — o /wallet mostra o valor atualizado.
    return (
      <StepFrame kbHeight={null}>
        <StepHeader title="Cartão" step="3/3" onBack={onDone} />
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-2 text-center">
          <div className="text-[15px] font-semibold" style={{ color: '#00A664' }}>
            Pagamento aprovado ✓
          </div>
          <p className="text-[13px]" style={{ color: MUTED }}>
            O saldo já foi creditado. Veja na sua carteira.
          </p>
        </div>
        <div className="flex-shrink-0 pb-2.5 pt-2.5">
          <Button variant="primary" onClick={onDone}>
            Ver carteira
          </Button>
        </div>
      </StepFrame>
    );
  }

  const busy = ui === 'processing' || ui === 'polling';
  // Picker de cartões salvos: só quando há salvos e o cliente não pediu "outro
  // cartão"; um recusado volta pro picker (usingNew segue false).
  const showPicker = savedCards.length > 0 && !usingNew && ui !== 'declined';

  if (showPicker) {
    return (
      <StepFrame kbHeight={null}>
        <StepHeader title="Cartão" step="3/3" onBack={onBack} />
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pt-1">
          <AmountHeader amount={amount} cashbackValue={cashbackValue} />
          <div className="flex flex-col gap-2">
            {savedCards.map((card) => (
              <button
                key={card.id}
                type="button"
                disabled={busy}
                onClick={() => onPaySaved(card)}
                className="flex items-center gap-3 rounded-[18px] bg-white px-4 py-3.5 text-left shadow-soft disabled:opacity-60"
              >
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-bow-royal/5 text-bow-royal">
                  <CardGlyph />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-bow-royal">
                    {formatCardLabel(card)}
                  </span>
                  <span className="block text-[11.5px]" style={{ color: MUTED }}>
                    {card.is_default ? 'Padrão · toque pra pagar' : 'Toque pra pagar'}
                  </span>
                </span>
                <span className="flex-shrink-0 text-[12px] font-semibold text-bow-royal">
                  {busy ? '…' : formatBRL(amount)}
                </span>
              </button>
            ))}
          </div>
          {error && (
            <p className="px-1 text-[12.5px]" style={{ color: DANGER }}>
              {error}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 pb-2.5 pt-2.5">
          <Button variant="secondary" onClick={() => setUsingNew(true)} disabled={busy}>
            Pagar com outro cartão
          </Button>
        </div>
      </StepFrame>
    );
  }

  return (
    <StepFrame kbHeight={null}>
      <StepHeader title="Cartão" step="3/3" onBack={onBack} />

      <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pt-1">
        <AmountHeader amount={amount} cashbackValue={cashbackValue} />

        {savedCards.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setUsingNew(false);
              setError(null);
            }}
            className="self-start px-1 text-[12px] font-semibold text-bow-royal"
          >
            ‹ Usar um cartão salvo
          </button>
        )}

        <div className="rounded-[20px] bg-white p-[18px] shadow-soft">
          <div className="mb-3 flex items-center gap-1.5" style={{ color: 'rgba(0,27,116,0.5)' }}>
            <LockIcon />
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em]">
              Ambiente criptografado
            </span>
          </div>
          <Field
            label="Número do cartão"
            value={number}
            onChange={(v) => setNumber(maskNumber(v))}
            placeholder="0000 0000 0000 0000"
            inputMode="numeric"
            autoComplete="cc-number"
            maxLength={19}
          />
          <div className="mt-3.5 flex gap-2.5">
            <Field
              label="Validade"
              value={expiry}
              onChange={(v) => setExpiry(maskExpiry(v))}
              placeholder="MM/AA"
              inputMode="numeric"
              autoComplete="cc-exp"
              maxLength={5}
            />
            <Field
              label="CVV"
              value={cvv}
              onChange={(v) => setCvv(v.replace(/\D/g, '').slice(0, 4))}
              placeholder="•••"
              inputMode="numeric"
              autoComplete="cc-csc"
              maxLength={4}
            />
          </div>
          <div className="mt-3.5">
            <Field
              label="Nome impresso no cartão"
              value={holder}
              onChange={setHolder}
              placeholder="Como aparece no cartão"
              autoComplete="cc-name"
            />
          </div>
        </div>

        <p className="flex items-start gap-2 px-1 text-[11.5px] leading-[1.45]" style={{ color: MUTED }}>
          <span className="mt-px flex-shrink-0" style={{ color: 'rgba(0,27,116,0.45)' }}>
            <LockIcon />
          </span>
          Criptografado no seu aparelho pelo provedor de pagamento — a Bow não recebe nem armazena o
          número do seu cartão.
        </p>

        {saveEnabled && (
          <label
            className="flex flex-shrink-0 items-center justify-between rounded-[18px] bg-white px-4 py-3"
            style={{ border: '1px solid rgba(0,27,116,0.08)' }}
          >
            <span className="text-[13px] font-semibold text-bow-royal">
              Salvar este cartão pra próxima
            </span>
            <Toggle on={storeCard} onChange={setStoreCard} label="Salvar este cartão" />
          </label>
        )}

        {error && (
          <p className="px-1 text-[12.5px]" style={{ color: DANGER }}>
            {error}
          </p>
        )}
      </form>

      <div className="flex-shrink-0 pb-2.5 pt-2.5">
        {ui === 'declined' ? (
          <div className="flex flex-col gap-2">
            <Button variant="primary" onClick={() => setUi('form')}>
              Tentar de novo
            </Button>
            <Button variant="secondary" onClick={onFallbackToPix}>
              Usar PIX
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            onClick={onSubmit as unknown as () => void}
            disabled={!canSubmit || ui === 'processing' || ui === 'polling' || ui === 'loading'}
          >
            {ui === 'processing'
              ? 'Processando…'
              : ui === 'polling'
                ? 'Confirmando…'
                : `Pagar no cartão · ${formatBRL(amount)}`}
          </Button>
        )}
        {showDevTools && lastChargeId && (
          <button
            type="button"
            className="mt-2 w-full text-[11px]"
            style={{ color: MUTED }}
            onClick={async () => {
              await paymentsApi.devSimulateCardPayment(lastChargeId).catch(() => null);
              await finishPaid();
            }}
          >
            [dev] simular pagamento
          </button>
        )}
      </div>
    </StepFrame>
  );
}

function AmountHeader({ amount, cashbackValue }: { amount: number; cashbackValue: number }) {
  return (
    <div className="flex flex-shrink-0 items-center justify-between rounded-2xl bg-white px-4 py-3.5">
      <MoneyDisplay value={amount} size={28} />
      {cashbackValue > 0 ? (
        <span className="text-[12px] font-semibold" style={{ color: '#A23C92' }}>
          + {formatBRL(cashbackValue)} cashback
        </span>
      ) : (
        <span className="text-[11.5px]" style={{ color: MUTED }}>
          cashback é benefício do PIX
        </span>
      )}
    </div>
  );
}

function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      className="relative flex-shrink-0 rounded-full transition-colors"
      style={{ width: 44, height: 26, background: on ? '#00C072' : 'rgba(0,27,116,0.15)' }}
    >
      <span
        className="absolute rounded-full bg-white transition-all"
        style={{ top: 2, left: on ? 20 : 2, width: 22, height: 22, boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}
      />
    </button>
  );
}

function CardGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="5" width="20" height="14" rx="2.5" />
      <path d="M2 10h20" />
    </svg>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: 'numeric' | 'text';
  autoComplete?: string;
  maxLength?: number;
}

function Field({ label, value, onChange, placeholder, inputMode, autoComplete, maxLength }: FieldProps) {
  const [focused, setFocused] = useState(false);
  return (
    <label className="flex flex-1 flex-col">
      <span className="mb-1.5 text-[11.5px] font-semibold" style={{ color: MUTED }}>
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="w-full bg-transparent text-[15px] font-medium text-bow-royal outline-none placeholder:font-normal"
        style={{
          height: 48,
          borderRadius: 13,
          background: '#FDFCF9',
          border: `1.5px solid ${focused ? '#001B74' : 'rgba(0,27,116,0.13)'}`,
          boxShadow: focused ? '0 0 0 3px rgba(0,27,116,0.08)' : 'none',
          padding: '0 14px'
        }}
      />
    </label>
  );
}

function maskNumber(v: string): string {
  const digits = v.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

function maskExpiry(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 4);
  return d.length <= 2 ? d : `${d.slice(0, 2)}/${d.slice(2)}`;
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}
